import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, SetStateAction, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import {createProcedureRunPlans, CreateProcedureRunPlansParams} from 'procedures/network/procedureRunPlanDAO';
import EntityStore from 'stores/EntityStore';
import MultiBatchSelector from 'studies/typed/process-development/multi-batch-selector/MultiBatchSelector';
import {useExperimentProcedure} from 'studies/typed/shared/ExperimentalInputs';
import {LabeledProcedureDropdown} from 'studies/typed/shared/LabeledProcedureDropdown';
import ProcedureRunDescriptionEditor from 'studies/typed/shared/ProcedureRunDescriptionEditor';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {refreshStudyOverview} from './TrayTaskUtils';

export interface AddProcedureRunPlanModalProps {
  onRequestClose: () => void;
  studyType: StudyType;
  studyId: string;
  initialProcedureId?: string;
}

function getButtons(submitDisabled: boolean): Array<ModalButton> {
  return [
    {cancel: true, disabled: false, text: 'Cancel'},
    {
      disabled: submitDisabled,
      submit: true,
      text: 'Plan',
      style: ButtonStyle.SECONDARY,
      dataTestAttrs: {component: 'AddProcedureRunPlanModal', element: 'submitButton'},
    },
  ];
}

function AddProcedureRunPlanModalContent(props: {
  procedureId: string | null;
  setProcedureId: Dispatch<SetStateAction<string | null>>;
  description: string;
  setDescription: Dispatch<SetStateAction<string>>;
  dataEntryMode: ProcedureRunDataEntryMode;
  setDataEntryMode: Dispatch<SetStateAction<ProcedureRunDataEntryMode>>;
  procedureType: ProcedureType;
  onRequestClose: () => void;
}): JSX.Element {
  return (
    <>
      <LabeledProcedureDropdown
        procedureId={props.procedureId}
        setProcedureId={props.setProcedureId}
        onRequestCloseModal={props.onRequestClose}
        procedureType={props.procedureType}
        requiredField={true}
        dropdownDisabled={false}
        // We need the latest possible procedure version id because that gets stamped into the new run
        procedureFetchPolicy="network-only"
      />
      <ProcedureRunDescriptionEditor
        setDescription={props.setDescription}
        description={props.description}
        isBeingCreated={true}
      />
      {props.procedureType === ProcedureType.RECIPE && (
        <MultiBatchSelector
          dataEntryMode={props.dataEntryMode}
          onSelectDataEntryMode={props.setDataEntryMode}
          isDisabled={false}
        />
      )}
    </>
  );
}

function isSubmitDisabled(procedureId: string | null, procedureLoaded: boolean): boolean {
  return procedureId == null || !procedureLoaded;
}

export default function AddProcedureRunPlanModal(props: AddProcedureRunPlanModalProps): JSX.Element {
  const procedureType = getProcedureTypeForStudyType(props.studyType);
  const [procedureId, setProcedureId] = useState<string | null>(props.initialProcedureId ?? null);
  const [description, setDescription] = useState<string>('');
  // PD studies have parallel execution pre-selected, while AD studies never use parallel execution.
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    props.studyType === StudyType.PROCESS_DEVELOPMENT
      ? ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER
      : ProcedureRunDataEntryMode.INDIVIDUAL_CONDITION_REPLICATES
  );
  const {procedure} = useExperimentProcedure(procedureId ? {procedureId} : null, props.studyType);

  const procedureFlowchartIds = useMemo(() => {
    if (procedure == null) {
      return null;
    }
    return {
      templateProcedureFlowchartVersionId: procedure.procedureFlowchartVersionId,
      workflowFlowchartConfigVersionId: procedure.workflowFlowchartConfigVersionId,
    };
  }, [procedure]);

  const currentUser = EntityStore.currentUser();
  assert(currentUser != null, 'Expected current user to be defined');

  const handleSubmit = async (): Promise<void> => {
    assert(procedureFlowchartIds != null, 'Expected procedure flowchart IDs to be defined');

    // Backend requires exactly one of these IDs to be provided (XOR)
    // If workflow config version exists (PoW-enabled), use it; otherwise use template version
    const createParams: CreateProcedureRunPlansParams = {
      description,
      dataEntryMode,
      creatorId: currentUser.id,
      templateProcedureFlowchartVersionId: procedureFlowchartIds.workflowFlowchartConfigVersionId
        ? null
        : procedureFlowchartIds.templateProcedureFlowchartVersionId,
      workflowFlowchartConfigVersionId: procedureFlowchartIds.workflowFlowchartConfigVersionId,
    };

    try {
      await withLoadingScreen(
        createProcedureRunPlans(props.studyId, [createParams]),
        `Creating ${getProcedureRunUIStringForStudyType(props.studyType)} plan...`
      );

      // Refetch the listing table to show the new plan
      refreshStudyOverview(props.studyId, props.studyType);

      notifications.success(
        `${getProcedureRunUIStringForStudyType(props.studyType, {capital: true})} plan created successfully`
      );

      props.onRequestClose();
    } catch (e) {
      if (net.isServerRequestError(e)) {
        notifications.error(
          <ToastMessage
            header={`Failed to create ${getProcedureRunUIStringForStudyType(props.studyType)} plan`}
            body={notifications.getFriendlyErrorAsElement(e, '')}
          />
        );
      } else {
        throw e;
      }
    }
  };

  const submitDisabled = isSubmitDisabled(procedureId, procedure != null);

  return (
    <Modal
      title={
        <div className={css(styles.titleWrapper)}>
          <span>Plan {getProcedureRunUIStringForStudyType(props.studyType)}</span>
        </div>
      }
      onRequestClose={() => {
        props.onRequestClose();
      }}
      onRequestSubmit={handleSubmit}
      buttons={getButtons(submitDisabled)}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
      mediumLarge={true}
    >
      <AddProcedureRunPlanModalContent
        procedureId={procedureId}
        setProcedureId={setProcedureId}
        description={description ?? ''}
        setDescription={setDescription}
        dataEntryMode={dataEntryMode}
        setDataEntryMode={setDataEntryMode}
        procedureType={procedureType}
        onRequestClose={props.onRequestClose}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  titleWrapper: {display: 'flex', flexWrap: 'wrap', columnGap: '50px', rowGap: 10},
  errorText: {color: Colors.RED, marginTop: -12},
});
